import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { BREATHING_EXERCISES, DURATIONS } from '@/config/breathingExercises';
import BreathingAnimation from '@/components/BreathingAnimation';
import BreathingHalo from '@/components/BreathingHalo';
import PremiumUpsellBreathing from './PremiumUpsellBreathing';
import { breathingVoiceScripts } from '@/data/breathingVoiceScripts';
import { playVoiceInstruction, stopVoiceInstruction, initBreathingVoice } from '@/services/breathingVoice';
import { getSosRespirationSounds } from '@/services/sosAudioService';

const BreathingExercise = ({ t }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setIsFullscreen } = useFullscreen();
  const isEnglish = t?.locale === 'en-US';
  const isFreeUser = !user?.isPremium && user?.subscription !== 'premium';

  const exercise = BREATHING_EXERCISES.find((ex) => ex.id === id);

  // State
  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState('inhale');
  const [phaseDuration, setPhaseDuration] = useState(0); // Durée de la phase actuelle
  const [totalTime, setTotalTime] = useState(0);
  const [duration, setDuration] = useState(180); // Default 3 min
  const [breathingSounds, setBreathingSounds] = useState([]);
  const [soundsLoaded, setSoundsLoaded] = useState(false);
  const [currentSoundIndex, setCurrentSoundIndex] = useState(0);
  const [volume, setVolume] = useState(0.5); // 50% volume by default
  
  // Guided voice states (PREMIUM only)
  const [voiceEnabled, setVoiceEnabled] = useState(true); // ON by default for Premium
  const [voiceVolume, setVoiceVolume] = useState(80); // 80% by default
  const isPremium = user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin';

  const intervalRef = useRef(null);
  const cycleIntervalRef = useRef(null);
  const audioRef = useRef(null);

  // Enable fullscreen mode
  useEffect(() => {
    // Note: Fullscreen disabled to keep TabBar visible for navigation
    // setIsFullscreen(true);
    return () => {
      // setIsFullscreen(false);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (cycleIntervalRef.current) clearInterval(cycleIntervalRef.current);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [setIsFullscreen]);

  // Load breathing sounds from R2 (via proxy to bypass CSP)
  useEffect(() => {
    const loadSounds = async () => {
      try {
        setSoundsLoaded(false);
        const sounds = await getSosRespirationSounds();
        
        if (sounds.length === 0) {
          setSoundsLoaded(true); // Mark as "loaded" even if empty to prevent infinite loading
        } else {
          // Map R2 sounds to the format expected by this component
          const mappedSounds = sounds.map(sound => ({
            url: sound.url,  // Already proxied URL
            name: sound.filename
          }));
          setBreathingSounds(mappedSounds);
          setSoundsLoaded(true); // Mark sounds as loaded
        }
      } catch (error) {
        console.error('❌ [BreathingExercise] Error loading breathing sounds:', error);
        setBreathingSounds([]);
        setSoundsLoaded(true); // Mark as loaded to prevent infinite loading
      }
    };
    loadSounds();
  }, []);

  // Guided voice synchronization with breathing phases
  // Voice enabled for all users on free exercises, premium-only for premium exercises
  // MOVED BEFORE CONDITIONAL RETURNS TO COMPLY WITH REACT HOOKS RULES
  useEffect(() => {
    if (!voiceEnabled || !isActive || !exercise) return;
    // Block voice for free users on premium exercises only
    if (isFreeUser && exercise.premium) return;
    
    const lang = isEnglish ? 'en' : 'fr';
    const script = breathingVoiceScripts[lang]?.[exercise.id];
    
    if (!script) {
      console.warn(`[BreathingVoice] No script found for exercise: ${exercise?.id}`);
      return;
    }
    
    const playPhaseVoice = () => {
      const volumeDecimal = voiceVolume / 100;
      
      // Get duration for current phase from exercise timings
      const currentPhaseDuration = exercise?.timings ? exercise.timings[phase] : 0;
      
      // Skip if no duration for this phase
      if (!currentPhaseDuration || currentPhaseDuration === 0) {
        return;
      }
      
      if (phase === 'inhale' && script.inhale) {
        playVoiceInstruction({ 
          text: script.inhale, 
          lang, 
          duration: currentPhaseDuration,
          volume: volumeDecimal, 
          voiceEnabled,
          phase: 'inhale'
        });
      } else if (phase === 'hold1' && script.hold1) {
        playVoiceInstruction({ 
          text: script.hold1, 
          lang, 
          duration: currentPhaseDuration,
          volume: volumeDecimal, 
          voiceEnabled,
          phase: 'hold1'
        });
      } else if (phase === 'hold' && script.hold) {
        playVoiceInstruction({ 
          text: script.hold, 
          lang, 
          duration: currentPhaseDuration,
          volume: volumeDecimal, 
          voiceEnabled,
          phase: 'hold1'
        });
      } else if (phase === 'exhale' && script.exhale) {
        playVoiceInstruction({ 
          text: script.exhale, 
          lang, 
          duration: currentPhaseDuration,
          volume: volumeDecimal, 
          voiceEnabled,
          phase: 'exhale'
        });
      } else if (phase === 'hold2' && script.hold2) {
        playVoiceInstruction({ 
          text: script.hold2, 
          lang, 
          duration: currentPhaseDuration,
          volume: volumeDecimal, 
          voiceEnabled,
          phase: 'hold2'
        });
      }
    };
    
    playPhaseVoice();
    
  }, [phase, isFreeUser, voiceEnabled, isActive, exercise, voiceVolume, isEnglish]);
  
  // Cleanup voice on unmount or stop
  // MOVED BEFORE CONDITIONAL RETURNS TO COMPLY WITH REACT HOOKS RULES
  useEffect(() => {
    return () => {
      stopVoiceInstruction();
    };
  }, []);

  // Check if exercise exists
  if (!exercise) {
    return (
      <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-xl mb-4">{isEnglish ? 'Exercise not found' : 'Exercice introuvable'}</p>
          <button onClick={() => navigate('/breathing-premium')} className="text-[#6F42EA] hover:underline">
            {isEnglish ? 'Back to exercises' : 'Retour aux exercices'}
          </button>
        </div>
      </div>
    );
  }

  // Check if FREE user tries to access premium exercise
  if (isFreeUser && exercise.premium) {
    return <PremiumUpsellBreathing t={t} />;
  }

  // Get current phase text
  const getPhaseText = () => {
    if (!isActive) return isEnglish ? 'Ready?' : 'Prêt ?';
    
    if (phase === 'inhale') return isEnglish ? 'Breathe in...' : 'Inspirez...';
    if (phase === 'hold1' || phase === 'hold2') return isEnglish ? 'Hold...' : 'Retenez...';
    if (phase === 'exhale') return isEnglish ? 'Breathe out...' : 'Expirez...';
    return '';
  };

  // Play audio with random selection
  const playAudio = () => {
    
    if (breathingSounds.length === 0) {
      return;
    }

    // Select random sound
    const randomIndex = Math.floor(Math.random() * breathingSounds.length);
    setCurrentSoundIndex(randomIndex);
    
    const sound = breathingSounds[randomIndex];
    // Use R2 proxy URL directly (already includes full path)
    const audioUrl = sound.url;
    
    console.log(`🎲 [BreathingExercise] Randomly selected sound ${randomIndex + 1}/${breathingSounds.length}: ${sound.name}`);
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    audioRef.current = new Audio(audioUrl);
    audioRef.current.volume = volume;
    audioRef.current.loop = true;
    
    audioRef.current.play()
      .then(() => {
      })
      .catch(err => {
        console.error('❌ [BreathingExercise] Error playing audio:', err);
        console.error('❌ [BreathingExercise] Audio URL:', audioUrl);
      });
  };

  // Stop audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  // Start exercise
  const startExercise = async () => {
    // V1.5: Initialize/unlock the breathing voice audio on user gesture (iOS Safari requirement)
    // This MUST happen synchronously in the click handler to work on iOS
    if (voiceEnabled && (!isFreeUser || !exercise?.premium)) {
      console.log('[BreathingExercise] 🔓 Initializing voice audio on user gesture');
      await initBreathingVoice();
    }
    
    if (!soundsLoaded) {
      // Wait for sounds to load before starting
      const checkSounds = setInterval(() => {
        if (soundsLoaded) {
          clearInterval(checkSounds);
          startExerciseInternal();
        }
      }, 100);
      return;
    }
    
    startExerciseInternal();
  };
  
  const startExerciseInternal = () => {
    setIsActive(true);
    setPhase('inhale');
    setTotalTime(0);
    playAudio();
    runBreathingCycle();
    startTimer();
  };

  // Stop exercise
  const stopExercise = () => {
    setIsActive(false);
    stopAudio();
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (cycleIntervalRef.current) {
      clearInterval(cycleIntervalRef.current);
      cycleIntervalRef.current = null;
    }
    setPhase('inhale');
    setTotalTime(0);
  };

  // Run breathing cycle
  const runBreathingCycle = () => {
    const { inhale, hold1, exhale, hold2 } = exercise.timings;
    const phases = [];

    if (inhale > 0) phases.push({ name: 'inhale', duration: inhale });
    if (hold1 > 0) phases.push({ name: 'hold1', duration: hold1 });
    if (exhale > 0) phases.push({ name: 'exhale', duration: exhale });
    if (hold2 > 0) phases.push({ name: 'hold2', duration: hold2 });

    let currentPhaseIndex = 0;
    let phaseStartTime = Date.now();

    // Set initial phase duration
    setPhaseDuration(phases[0].duration);

    const updatePhase = () => {
      const elapsed = (Date.now() - phaseStartTime) / 1000;
      const currentPhase = phases[currentPhaseIndex];

      if (elapsed >= currentPhase.duration) {
        // Move to next phase
        currentPhaseIndex = (currentPhaseIndex + 1) % phases.length;
        phaseStartTime = Date.now();
        setPhase(phases[currentPhaseIndex].name);
        setPhaseDuration(phases[currentPhaseIndex].duration); // Update phase duration for halo
        console.log('Phase changed to:', phases[currentPhaseIndex].name, 'duration:', phases[currentPhaseIndex].duration);
      }
    };

    cycleIntervalRef.current = setInterval(updatePhase, 100);
  };

  // Timer
  const startTimer = () => {
    intervalRef.current = setInterval(() => {
      setTotalTime((prev) => {
        const newTime = prev + 1;
        if (newTime >= duration) {
          stopExercise();
          setTimeout(() => {
            alert(isEnglish ? 'Session complete!' : 'Séance terminée !');
            navigate('/breathing-premium');
          }, 500);
        }
        return newTime;
      });
    }, 1000);
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="relative min-h-screen bg-[#0A0F1C] pb-32">
      {/* Background gradient */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background: 'linear-gradient(180deg, #151A24 0%, #441A7A 100%)',
        }}
      />

      {/* Main container - Vertical Flexbox Structure */}
      <div 
        className="relative z-10 h-full flex flex-col" 
        style={{ paddingTop: 'env(safe-area-inset-top, 20px)' }}
      >
        
        {/* ===== HEADER SECTION (Fixed Height) ===== */}
        <div className="flex-shrink-0 px-4 pt-4">
          {/* Timer */}
          <div className="flex items-center justify-center pb-3">
            <div className="text-white text-xl font-bold">
              {formatTime(totalTime)} / {formatTime(duration)}
            </div>
          </div>

          {/* Duration selector (when not active) */}
          {!isActive && (
            <div className="mb-3">
              <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 max-w-lg mx-auto">
                <p className="text-white text-sm font-semibold mb-3 text-center">
                  {isEnglish ? 'Duration' : 'Durée'}
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {DURATIONS.map((dur) => (
                    <button
                      key={dur.value}
                      onClick={() => setDuration(dur.value)}
                      className={`px-3 py-2 rounded-lg transition-all font-semibold text-sm ${
                        duration === dur.value
                          ? 'bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] text-white shadow-lg scale-105'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {dur.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Exercise name */}
          <div className="text-center pb-2">
            <h2 className="text-white text-2xl font-bold mb-1">
              {isEnglish ? exercise.nameEn : exercise.name}
            </h2>
            <p className="text-white/60 text-xs italic">
              {isEnglish ? 'Not counted as soundscapes' : 'Non compté comme écoute'}
            </p>
          </div>
        </div>

        {/* ===== MAIN SECTION (Flexible Growth - Animation Zone) ===== */}
        <div className="flex-grow flex flex-col items-center justify-center px-6 py-2 min-h-0">
          {/* Animation container with halo */}
          <div className="w-full max-w-sm relative">
            {/* Halo dynamique progressif - PREMIUM uniquement */}
            <BreathingHalo 
              phase={phase} 
              phaseDuration={phaseDuration} 
              isActive={isActive} 
            />
            {/* Animation principale */}
            <BreathingAnimation phase={phase} duration={exercise.timings[phase]} isActive={isActive} />
          </div>

          {/* Rhythm indicator */}
          <p className="text-white/60 text-sm font-mono mt-3">{exercise.rhythm}</p>

          {/* Phase text */}
          <p className="text-white text-lg font-light mt-1">{getPhaseText()}</p>
        </div>

        {/* ===== FOOTER SECTION (Fixed Height - Controls) ===== */}
        <div className="flex-shrink-0 px-6 pb-8 pt-2">
          {isActive ? (
            /* Volume control + Stop button when active - Stacked layout to avoid overlap */
            <div className="space-y-2">
              {/* Guided voice controls - available for free exercise too */}
              {(!isFreeUser || !exercise?.premium) && (
                <div className="max-w-sm mx-auto">
                  <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5 border border-white/10">
                    {/* Toggle Guided Voice */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white text-xs font-semibold">
                        {isEnglish ? '🎙️ Guided voice' : '🎙️ Voix guidée'}
                      </span>
                      <button
                        onClick={() => {
                          setVoiceEnabled(!voiceEnabled);
                          if (!voiceEnabled) {
                          } else {
                            stopVoiceInstruction();
                          }
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          voiceEnabled ? 'bg-[#6F42EA]' : 'bg-white/20'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            voiceEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {/* Voice volume slider - Only visible when voice is enabled */}
                    {voiceEnabled && (
                      <>
                        <div className="flex items-center justify-between mb-1.5 mt-3">
                          <span className="text-white/70 text-xs">
                            {isEnglish ? 'Voice volume' : 'Volume voix'}
                          </span>
                          <span className="text-white/70 text-xs">{voiceVolume}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={voiceVolume}
                          onChange={(e) => setVoiceVolume(parseInt(e.target.value))}
                          className="w-full h-1.5 bg-white/20 rounded-lg appearance-none cursor-pointer"
                          style={{
                            background: `linear-gradient(to right, #6F42EA 0%, #6F42EA ${voiceVolume}%, rgba(255,255,255,0.2) ${voiceVolume}%, rgba(255,255,255,0.2) 100%)`,
                            touchAction: 'none',
                            padding: '14px 0',
                            boxSizing: 'content-box'
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
              
              
              {/* Stop button */}
              <div className="flex justify-center mt-1">
                <button
                  onClick={() => {
                    stopExercise();
                  }}
                  className="px-12 py-2.5 bg-red-500/80 backdrop-blur-md hover:bg-red-500 text-white text-base font-semibold rounded-full border border-red-400/50 transition-all shadow-lg"
                >
                  {isEnglish ? 'Stop' : 'Arrêter'}
                </button>
              </div>
            </div>
          ) : (
            /* Start button + Back button when not active - More spacing from content above */
            <div className="space-y-3">
              {/* Start button - Primary action with top margin to avoid overlap with 4-4-4-4 */}
              <div className="flex justify-center mt-6">
                <button
                  onClick={startExercise}
                  className="w-full max-w-xs px-10 py-3 bg-gradient-to-r from-[#6F42EA] to-[#8B5CF6] hover:from-[#5F32DA] hover:to-[#7B4CE6] text-white text-lg font-semibold rounded-full shadow-[0_4px_30px_rgba(111,66,234,0.5)] hover:shadow-[0_6px_40px_rgba(111,66,234,0.7)] transition-all"
                >
                  {isEnglish ? 'Start' : 'Commencer'}
                </button>
              </div>
              
              {/* Back button - Secondary action, positioned below */}
              <div className="flex justify-center">
                <button
                  onClick={() => navigate('/breathing-premium')}
                  className="p-3 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 text-white transition-all"
                  title={isEnglish ? 'Back' : 'Retour'}
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreathingExercise;
